import React from "react";
import PropTypes from "prop-types";
import { I18n } from "@aws-amplify/core";
import { withStyles } from "@material-ui/styles";
import { max, includes, forEach, get } from "lodash";

import { fetchGet } from "../../api/fetch";
//import { API_MAIN } from "../../constants/api";

import Grid from "@material-ui/core/Grid";
import Box from "@material-ui/core/Box";

import CloseIcon from "./CloseIcon";

import images from "../../assets/img/images";
import colors from "../../assets/style/colors";

const styles = {
  canvasContainer: {
    border: `1px solid ${colors.black542}`,
    borderRadius: "2px",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  zoomedBox: {
    zIndex: "1200",
    width: "100vw",
    height: "100vh",
    position: "fixed",
    top: 0,
    left: 0,
    backgroundColor: colors.black87,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  zoomIcon: {
    position: "absolute",
    top: "30px",
    display: "inline-block",
    cursor: "pointer",
    height: "36px",
  },
};

const PictureROI = (props) => {
  const {
    classes,
    url,
    roiOverlay,
    thermalPoint,
    initialSquareSize,
    blackbody,
    landmarks,
    id,
    powerMode,
    jsonSample,
  } = props;
  const [showRoi, setShowRoi] = React.useState(true);
  const [imgSize, setImgSize] = React.useState({ width: 0, height: 0 });
  const [scale, setScale] = React.useState(1);
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const [isZoomed, setIsZoomed] = React.useState(false);
  const [squareSize, setSquareSize] = React.useState(initialSquareSize);

  const [image, setImage] = React.useState();

  React.useEffect(() => {
    downloadImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const downloadImage = async () => {
    if (url) {
      try {
        let res = await fetchGet(getUrl(url));
        setImage(res);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const getUrl = (url) => {
    let res = "";
    if (includes(url, "/api/images/")) {
      let index = url.indexOf("/api/images/") + 12;
      url = url.slice(index);
      res = `images/${url}`;
    } else {
      res = `images/${url}`;
    }
    return res;
  };

  React.useEffect(() => {
    setShowRoi(props.showRoi);
  }, [props.showRoi]);

  React.useEffect(() => {
    setOffset({ x: 0, y: 0 });
    setZoom(1);
    setScale(1);
    setIsZoomed(false);
    setSquareSize(initialSquareSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  React.useEffect(() => {
    const canvas = document.getElementById(`canvas_${id}`);
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const img = new Image();
      img.onload = function () {
        ctx.save();

        // Apply scale to image to fit into canvas
        const scale =
          zoom *
          (canvas.width / canvas.height < this.width / this.height
            ? canvas.width / this.width
            : canvas.height / this.height);
        ctx.scale(scale, scale);

        let translation = {
          x: 0,
          y: 0,
        };
        if (roiOverlay && zoom > 1) {
          // Center image on ROI center
          translation = {
            x: Math.round(
              (canvas.width / 2 -
                (roiOverlay.x1 + (roiOverlay.x2 - roiOverlay.x1) / 2) * scale) /
                scale
            ),
            y: Math.round(
              (canvas.height / 2 -
                (roiOverlay.y1 + (roiOverlay.y2 - roiOverlay.y1) / 2) * scale) /
                scale
            ),
          };
        } else {
          translation = {
            x: Math.round(
              (squareSize.width - this.width * scale) / (2 * scale)
            ),
            y: Math.round(
              (squareSize.height - this.height * scale) / (2 * scale)
            ),
          };
        }
        ctx.translate(translation.x, translation.y);
        ctx.drawImage(img, 0, 0);

        setScale(scale);
        setImgSize({ width: this.width, height: this.height });
        setOffset(translation);

        ctx.strokeStyle = colors.blueROI;
        ctx.lineWidth = (isZoomed ? 3 : 2) / scale;
        if (showRoi && roiOverlay) {
          ctx.strokeRect(
            roiOverlay.x1 - 2,
            roiOverlay.y1 - 2,
            roiOverlay.x2 - roiOverlay.x1 + 4,
            roiOverlay.y2 - roiOverlay.y1 + 4
          );
        }
        // Blackbody
        if (blackbody) {
          ctx.strokeRect(
            blackbody.x1 - 2,
            blackbody.y1 - 2,
            blackbody.x2 - blackbody.x1 + 4,
            blackbody.y2 - blackbody.y1 + 4
          );
        }
        // Landmarks
        if (powerMode && landmarks) {
          forEach(landmarks, (item) => {
            if (get(item, ["confidence"]) > 0.5) {
              ctx.fillStyle = colors.bodyPink;
              ctx.fillRect(
                get(item, ["coord", "x"]) + get(jsonSample, ["face", "x1"]),
                get(item, ["coord", "y"]) + get(jsonSample, ["face", "y1"]),
                max([canvas.width < 500 ? 2 / scale : 4 / scale, 1 / scale]),
                max([canvas.width < 500 ? 2 / scale : 4 / scale, 1 / scale])
              );
            }
          });
        }
        if (thermalPoint) {
          ctx.fillStyle = colors.threatRed;
          ctx.fillRect(
            thermalPoint.x,
            thermalPoint.y,
            max([canvas.width < 500 ? 3 / scale : 6 / scale, 1 / scale]),
            max([canvas.width < 500 ? 3 / scale : 6 / scale, 1 / scale])
          );
        }
        ctx.restore();
      };
      img.src = image;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRoi, zoom, isZoomed, image, powerMode]);

  // const getUrl = (url) => {
  //   if ((url && props.local) || url.includes("http")) {
  //     return url;
  //   }
  //   return `/${API_MAIN}/images/${url}`;
  // };

  const onZoom = (zoomType) => {
    if (zoomType === "zoomIn") {
      setZoom(zoom * 2);
    } else if (zoomType === "zoomOut") {
      setZoom(zoom / 2);
    }
  };

  if (!url) {
    return (
      <Box
        className="text-54 text-center"
        style={{
          width: `${squareSize.width}px`,
          height: `${squareSize.height}px`,
          lineHeight: `${squareSize.height}px`,
        }}
      >
        {squareSize.width >= 70 && I18n.get("site_noPhoto")}
      </Box>
    );
  }

  if (isZoomed) {
    return (
      <Box className={classes.zoomedBox}>
        <CloseIcon
          closeFunction={() => {
            setIsZoomed(false);
            setSquareSize(initialSquareSize);
          }}
          darkMode={true}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
          }}
        />
        <canvas
          id={`canvas_${id}`}
          style={{ cursor: "zoom-out" }}
          onClick={() => {
            setIsZoomed(false);
            setSquareSize(initialSquareSize);
          }}
          width={imgSize.width * scale}
          height={imgSize.height * scale}
        />
      </Box>
    );
  }

  return (
    <Grid
      item
      className={classes.canvasContainer}
      style={{
        height: squareSize.height,
        width: squareSize.width,
        cursor: props.onClick ? "pointer" : "default",
      }}
    >
      <canvas
        id={`canvas_${id}`}
        onClick={props.onClick}
        height={squareSize.height}
        width={squareSize.width}
        onMouseMove={(e) => {
          if (props.onMouseMove) {
            props.onMouseMove(e, imgSize, scale, offset, zoom);
          }
        }}
      />
      {!props.noZoom && (
        <Box component="span" className={classes.zoomIcon}>
          <Box component="span" style={{ marginRight: "24px" }}>
            <img
              src={images.zoomOutGreen}
              alt="zoom_out"
              onClick={() => onZoom("zoomOut")}
            />
          </Box>
          <Box component="span">
            <img
              src={images.zoomInGreen}
              alt="zoom_in"
              onClick={() => onZoom("zoomIn")}
            />
          </Box>
        </Box>
      )}
    </Grid>
  );
};

PictureROI.propTypes = {
  url: PropTypes.string,
  roiOverlay: PropTypes.object,
  initialSquareSize: PropTypes.object,
  id: PropTypes.string,
};

export default withStyles(styles)(PictureROI);
